/**
 * B2 · CHAT DEL AULA — hooks local-first.
 *
 * Los mensajes llegan por sync (`my_cohort_chat` para quien cursa,
 * `my_cohort_chat_teacher` para quien modera), así que la conversación se
 * escribe sin conexión y se entrega al reconectar. La identidad de quien
 * escribe NO viaja con ella: se hidrata por REST con `useCohortIdentities`
 * (`GET /api/cohorts/:id/roster`), que es la fuente única de identidad del
 * cliente desde el plan 06 · E3.
 *
 * Lo que NO hay aquí, a propósito: presencia en vivo ni «está escribiendo».
 * 300 alumnos latiendo cada 30 s serían ~15.000 entregas de fila por minuto por
 * un dato que caduca en medio minuto. El «visto por última vez» se DERIVA de
 * los mensajes ya sincronizados (`lastSeenByAuthor`), con coste cero.
 */
import { type Accessor, createMemo } from "solid-js";
import {
  type ChatReactionEmoji,
  chatReactionId,
  classroomChatQueryKeys,
  countUnreadMessages,
  deleteChatMessageMutation,
  editChatMessageMutation,
  getCohortChatMessagesQuery,
  getCohortChatReactionsQuery,
  getMyChatReadQuery,
  linkChatMessageToThreadMutation,
  markChatReadMutation,
  reactToChatMessageMutation,
  removeChatReactionMutation,
  sendChatMessageMutation,
  setChatMessageHiddenMutation,
  setChatMessagePinnedMutation,
  setChatMutedMutation,
} from "@space/shared";
import { createPowerSyncQuery, createPowerSyncSingle } from "@space/shared/solid";
import type { CohortChatMessage, CohortChatReaction, CohortChatRead } from "@space/powersync";
import { usePowerSyncContext } from "@/providers/power-sync-context";
import { useDrizzle } from "@/hooks/use-drizzle";
import { useSession } from "@/lib/auth";
import { OFFLINE_CACHE_STALE_TIME_MS } from "@/lib/constants";

function useBase() {
  const { db } = usePowerSyncContext();
  const drizzle = useDrizzle();
  const { userId } = useSession();
  return { db, drizzle, userId, staleTime: OFFLINE_CACHE_STALE_TIME_MS };
}

/** La conversación de una cohorte (la ventana viva que entrega el stream). */
export function useCohortChatMessages(cohortId: Accessor<string | undefined>) {
  const base = useBase();
  return createPowerSyncQuery<CohortChatMessage>({
    queryKey: () => classroomChatQueryKeys.messages(cohortId()),
    db: base.db,
    queryFn: () => {
      const db = base.drizzle();
      const id = cohortId();
      return db && id ? getCohortChatMessagesQuery(db, id) : null;
    },
    enabled: () => Boolean(cohortId()) && Boolean(base.drizzle()),
    staleTime: base.staleTime,
    suspense: false,
  });
}

/** MI marca de leído / silencio del aula. */
export function useMyChatRead(cohortId: Accessor<string | undefined>) {
  const base = useBase();
  return createPowerSyncSingle<CohortChatRead>({
    queryKey: () => classroomChatQueryKeys.read(cohortId(), base.userId()),
    db: base.db,
    queryFn: () => {
      const db = base.drizzle();
      const id = cohortId();
      const uid = base.userId();
      return db && id && uid ? getMyChatReadQuery(db, id, uid) : null;
    },
    enabled: () => Boolean(cohortId()) && Boolean(base.userId()) && Boolean(base.drizzle()),
    staleTime: base.staleTime,
    suspense: false,
  });
}

/** Reacciones de la ventana viva (etapa con flag propio: puede venir vacío). */
export function useCohortChatReactions(cohortId: Accessor<string | undefined>) {
  const base = useBase();
  return createPowerSyncQuery<CohortChatReaction>({
    queryKey: () => classroomChatQueryKeys.reactions(cohortId()),
    db: base.db,
    queryFn: () => {
      const db = base.drizzle();
      const id = cohortId();
      return db && id ? getCohortChatReactionsQuery(db, id) : null;
    },
    enabled: () => Boolean(cohortId()) && Boolean(base.drizzle()),
    staleTime: base.staleTime,
    suspense: false,
  });
}

/**
 * Contador de no-leídos de un aula. Se calcula sobre lo YA sincronizado (no
 * hay endpoint que preguntar) y se usa tanto en la burbuja de la pestaña como
 * en la tarjeta de la lista de aulas.
 */
export function useCohortChatUnread(cohortId: Accessor<string | undefined>): Accessor<number> {
  const { userId } = useSession();
  const messages = useCohortChatMessages(cohortId);
  const read = useMyChatRead(cohortId);
  const unread = createMemo(() => {
    const uid = userId();
    if (!uid) {
      return 0;
    }
    return countUnreadMessages(messages.data ?? [], uid, read.data?.last_read_at ?? null);
  });
  return unread;
}

/** Acciones de escritura del chat (todas pasan por el write-path del server). */
export function useChatActions(
  cohortId: Accessor<string | undefined>,
  orgId?: Accessor<string | undefined>,
) {
  const base = useBase();
  const requireDb = () => {
    const db = base.drizzle();
    const id = cohortId();
    const uid = base.userId();
    if (!db || !id || !uid) {
      return null;
    }
    return { db, id, uid };
  };
  return {
    send: async (body: string) => {
      const ctx = requireDb();
      if (!ctx || !body.trim()) {
        return;
      }
      await sendChatMessageMutation(ctx.db, {
        cohortId: ctx.id,
        organizationId: orgId?.() ?? null,
        authorId: ctx.uid,
        body,
      });
    },
    edit: async (messageId: string, body: string) => {
      const ctx = requireDb();
      if (ctx) {
        await editChatMessageMutation(ctx.db, messageId, body);
      }
    },
    remove: async (messageId: string) => {
      const ctx = requireDb();
      if (ctx) {
        await deleteChatMessageMutation(ctx.db, messageId);
      }
    },
    setHidden: async (messageId: string, hidden: boolean) => {
      const ctx = requireDb();
      if (ctx) {
        await setChatMessageHiddenMutation(ctx.db, messageId, hidden);
      }
    },
    setPinned: async (messageId: string, pinned: boolean) => {
      const ctx = requireDb();
      if (ctx) {
        await setChatMessagePinnedMutation(ctx.db, messageId, pinned);
      }
    },
    linkToThread: async (messageId: string, threadId: string) => {
      const ctx = requireDb();
      if (ctx) {
        await linkChatMessageToThreadMutation(ctx.db, messageId, threadId);
      }
    },
    markRead: async (at?: string) => {
      const ctx = requireDb();
      if (ctx) {
        await markChatReadMutation(ctx.db, {
          cohortId: ctx.id,
          organizationId: orgId?.() ?? null,
          userId: ctx.uid,
          at,
        });
      }
    },
    setMuted: async (mutedUntil: string | null) => {
      const ctx = requireDb();
      if (ctx) {
        await setChatMutedMutation(ctx.db, {
          cohortId: ctx.id,
          organizationId: orgId?.() ?? null,
          userId: ctx.uid,
          mutedUntil,
        });
      }
    },
    /** Alterna MI reacción: la misma dos veces la retira. */
    toggleReaction: async (messageId: string, emoji: ChatReactionEmoji, mine: string | null) => {
      const ctx = requireDb();
      if (!ctx) {
        return;
      }
      if (mine === emoji) {
        await removeChatReactionMutation(ctx.db, messageId, ctx.uid);
        return;
      }
      await reactToChatMessageMutation(ctx.db, {
        messageId,
        cohortId: ctx.id,
        organizationId: orgId?.() ?? null,
        userId: ctx.uid,
        emoji,
      });
    },
    /** Id determinista de mi reacción (para pruebas y depuración). */
    reactionId: (messageId: string) => chatReactionId(messageId, base.userId() ?? ""),
  };
}
